/**
 * Deterministic Project Intent / Overview prose for the report section.
 * Used as fallback when LLM-authored `projectIntentOverview` is absent.
 */
import type { ReportTypeId } from '../config/reportTypes';
import type { PowerAvailabilityPreferences, ProjectIntent } from '../types/siteRag';
import type { SiteRagReport } from '../types/siteRag';
import { summarizeProjectIntent } from './projectIntentSummary';
import { humanizeIntentSummary } from './intentContextFormat';
import { REACTOR_DESIGN_V1_SCOPE_NOTE, intentShowsReactorDesignPicker } from '../config/reactorDesignCatalog';
import {
  filterRenderableParagraphs,
  isRenderableNarrative,
  safeExecSummarySnippet,
  stripAppOnboardingCopy,
} from './safeNarrativeProse';

export interface ProjectIntentOverviewInput {
  siteName: string;
  address?: string;
  projectIntent?: ProjectIntent;
  executiveSummarySnippet?: string;
  reportType?: ReportTypeId | null;
  powerSetup?: PowerAvailabilityPreferences;
}

interface TypeFraming {
  /** Closing sentence after the intent summary. */
  closing: string;
}

const TYPE_FRAMING: Record<ReportTypeId, TypeFraming> = {
  feasibility: {
    closing: 'All feasibility scoring, behind-the-meter technology comparisons, and development pathway recommendations in this document are framed against these goals.',
  },
  environmental: {
    closing: 'Permitting pathway screening, ecological assessments, and mitigation recommendations in this environmental diligence report are framed against these deployment goals.',
  },
  'power-availability': {
    closing: 'Grid capacity analysis, interconnection queue review, and BTM technology comparisons in this power-availability report are framed against these goals.',
  },
  'water-resource': {
    closing: 'Water supply, rights, drought resilience, and cooling-demand sections in this water-resource report are framed against these goals.',
  },
  'test-fit': {
    closing: 'Parcel layout, setback compliance, and buildout capacity sections in this test-fit report are framed against these goals.',
  },
  interconnection: {
    closing: 'Fiber availability, latency modeling, and connectivity risk sections in this interconnection report are framed against these goals.',
  },
  'supply-chain-risk': {
    closing: 'Procurement posture, lead-time critical path, and supplier risk sections in this supply-chain diligence report are framed against these goals.',
  },
  'custom-brief': {
    closing: 'Diligence Q&A responses in this custom brief are framed against these goals.',
  },
  'public-sentiment': {
    closing: 'Stakeholder mapping, sentiment themes, and mitigation playbook sections in this public sentiment report are framed against these goals.',
  },
  'executive-summary': {
    closing: 'Cross-report synthesis, BTM viability assessment, and recommended next steps in this executive brief are framed against these goals.',
  },
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report framing.
  financial: {
    closing: 'LCOE, NPV / IRR-vs-hurdle, payback, Monte-Carlo probability, and the pathway-economics comparison in this financial viability report are framed against these goals.',
  },
};

function resolveReportType(reportType: ReportTypeId | null | undefined): ReportTypeId {
  return reportType ?? 'feasibility';
}

function buildPowerSetupParagraph(prefs: PowerAvailabilityPreferences, projectIntent?: ProjectIntent): string | null {
  const parts: string[] = [];
  const mw = projectIntent?.powerRequirementMW;
  if (typeof mw === 'number' && Number.isFinite(mw)) {
    parts.push(`target capacity ${mw} MW`);
  }
  if (prefs.btmStrategy) parts.push(`BTM strategy ${prefs.btmStrategy}`);
  if (prefs.renewablesPreference) parts.push(`renewables preference ${prefs.renewablesPreference}`);
  if (prefs.curtailmentRiskTolerance) parts.push(`curtailment tolerance ${prefs.curtailmentRiskTolerance}`);
  if (prefs.timeToPowerCriticalDate) parts.push(`time-to-power critical date ${prefs.timeToPowerCriticalDate}`);
  if (typeof prefs.maxSubstationDistanceMiAmber === 'number') {
    parts.push(`substation amber threshold ${prefs.maxSubstationDistanceMiAmber} mi`);
  }
  if (typeof prefs.maxSubstationDistanceMiRed === 'number') {
    parts.push(`substation red threshold ${prefs.maxSubstationDistanceMiRed} mi`);
  }
  if (parts.length === 0) return null;
  return `Power report setup: ${parts.join('; ')}. Subsequent grid and BTM sections apply these constraints.`;
}

import { isScreeningRecommendationMode } from './intent/screeningIntentMode';

/** Expand the compact intent chip string into 1–2 readable paragraphs. */
export function buildProjectIntentOverview(input: ProjectIntentOverviewInput): string | null {
  const { siteName, address, projectIntent, executiveSummarySnippet, powerSetup } = input;
  const reportType = resolveReportType(input.reportType);
  const framing = TYPE_FRAMING[reportType];
  const summary = summarizeProjectIntent(projectIntent);
  const recommendMode = isScreeningRecommendationMode(projectIntent);
  if (!summary && !address && !recommendMode) return null;

  const paragraphs: string[] = [];

  if (recommendMode) {
    if (summary) {
      paragraphs.push(
        `This report screens ${siteName}${address ? ` at ${address}` : ''} in Recommend-an-Approach mode against the declared project intent: ${summary}. Remaining capacity envelope, power delivery architecture, and cooling strategy are recommended from verified site evidence. `
        + framing.closing,
      );
    } else {
      paragraphs.push(
        `This report screens ${siteName}${address ? ` at ${address}` : ''} in Recommend-an-Approach mode (open-brief discovery) to identify the recommended screening capacity range, behind-the-meter or grid power architecture, and cooling strategy grounded in verified site evidence. `
        + framing.closing,
      );
    }
    if (projectIntent && intentShowsReactorDesignPicker(projectIntent)) {
      paragraphs.push(REACTOR_DESIGN_V1_SCOPE_NOTE);
    }
  } else if (summary) {
    const modePrefix = projectIntent?.screeningMode === 'evaluate-proposal'
      ? `This report evaluates ${siteName}${address ? ` at ${address}` : ''} in Evaluate-My-Proposal mode against the declared project intent: ${summary}. `
      : `This report evaluates ${siteName}${address ? ` at ${address}` : ''} against the declared project intent: ${summary}. `;
    paragraphs.push(modePrefix + framing.closing);
    if (projectIntent && intentShowsReactorDesignPicker(projectIntent)) {
      paragraphs.push(REACTOR_DESIGN_V1_SCOPE_NOTE);
    }
  } else if (address) {
    // Redhead review wave 3 (defect 7) — the report body carries ONLY the
    // factual evaluate sentence. The "Set Project Intent in site
    // configuration…" call-to-action is app onboarding copy and must never
    // ship inside report prose (in-app empty states keep their own copy).
    paragraphs.push(`This report evaluates ${siteName} at ${address}.`);
  }

  if (reportType === 'power-availability' && powerSetup) {
    const setupPara = buildPowerSetupParagraph(powerSetup, projectIntent);
    if (setupPara) paragraphs.push(setupPara);
  }

  if (executiveSummarySnippet && executiveSummarySnippet.trim().length > 40) {
    if (isRenderableNarrative(executiveSummarySnippet)) {
      const trimmed = executiveSummarySummaryTrim(executiveSummarySnippet);
      if (trimmed && isRenderableNarrative(trimmed)) {
        paragraphs.push(trimmed);
      }
    }
  }

  return paragraphs.length > 0 ? paragraphs.join('\n\n') : null;
}

/** Normalized paragraph key for the cover-brief / caveat de-dup (citation
 *  tokens + whitespace + trailing punctuation stripped, lowercased). */
function overviewParaKey(s: string): string {
  return s
    .trim()
    .replace(/\[Ref\s*\d+\]|\[\d+\]/gi, '')
    .replace(/\s+/g, ' ')
    .replace(/[.,;:\s]+$/, '')
    .toLowerCase();
}

/**
 * OVERVIEW-CLEANUP (William 2026-07-04) — the reader-facing post-process for the
 * project-intent overview prose, run at the resolve boundary so it heals BOTH
 * the LLM-authored prose AND the deterministic fallback (and every legacy
 * persisted report, no regen):
 *   1. INTENT-HUMANIZE-1 — the machine intent summary the LLM echoed after
 *      "declared project intent:" is rewritten to reader prose (no "OR:/AND:"
 *      editor prefixes, no repeated utility clause, ISO dates → "Dec 2027").
 *   2. BRIEF-DEDUP-1 — the executive-summary lede is rendered IN FULL as the
 *      cover "IN BRIEF"; a paragraph here that repeats it verbatim is dropped
 *      (the cover is the single home for the brief).
 *   3. the standalone "Generic design class only…" v1-scope caveat is dropped
 *      from the overview (it reads as a dangling note; the reactor scope belongs
 *      to the BTM technology section).
 */
function cleanupOverviewProse(
  prose: string,
  execSummary: string | undefined,
  projectIntent?: ProjectIntent,
  legacySummary?: string,
): string {
  const humanized = prose.replace(
    /(declared project intent:\s*)([^.]*?)(\.\s)/,
    (_m, pre: string, summary: string, post: string) =>
      pre + (humanizeIntentSummary(summary, projectIntent) || summary) + post,
  );
  const reactorKey = overviewParaKey(REACTOR_DESIGN_V1_SCOPE_NOTE);
  const execKeys = [
    ...(execSummary ? execSummary.split(/\n\n+/) : []),
    ...(legacySummary ? legacySummary.split(/\n\n+/) : []),
  ]
    .map(overviewParaKey)
    .filter((k) => k.length > 40);
  const kept = humanized
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .filter((p) => {
      const k = overviewParaKey(p);
      if (k === reactorKey) return false;
      // drop a paragraph that IS (or fully contains / is contained by) the
      // executive-summary lede — the cover-brief duplicate.
      if (execKeys.some((ek) => ek === k || (k.length > 40 && (ek.includes(k) || k.includes(ek))))) return false;
      return true;
    });
  return kept.join('\n\n');
}

/** Resolve overview prose for render/export — filters leaks and rebuilds when needed. */
export function resolveProjectIntentOverviewProse(
  report: Pick<
    SiteRagReport,
    | 'projectIntentOverview'
    | 'siteName'
    | 'address'
    | 'projectIntentAtGen'
    | 'executiveSummary'
    | 'reportType'
    | 'sectionGuardStatus'
    | 'typedPreferences'
  > & { summary?: string },
): string | null {
  const authored = report.projectIntentOverview?.trim();
  const guard = report.sectionGuardStatus?.projectIntentOverview;

  if (authored && guard !== 'degraded-instruction-leak') {
    // Redhead review wave 3 (defect 7) — legacy generated reports persisted
    // the "Set Project Intent in site configuration…" app-onboarding CTA into
    // the overview prose; strip it at the resolve boundary so no existing
    // report renders/exports it (regeneration not required).
    const filtered = stripAppOnboardingCopy(filterRenderableParagraphs(authored));
    const bareAddressStub = `This report evaluates ${report.siteName}${report.address ? ` at ${report.address}` : ''}.`;
    const isBareRecommendStub = isScreeningRecommendationMode(report.projectIntentAtGen)
      && filtered.trim() === bareAddressStub;
    if (filtered && !isBareRecommendStub) {
      return cleanupOverviewProse(filtered, report.executiveSummary, report.projectIntentAtGen, report.summary) || null;
    }
  }

  const fallback = buildProjectIntentOverview({
    siteName: report.siteName,
    address: report.address,
    projectIntent: report.projectIntentAtGen,
    executiveSummarySnippet: safeExecSummarySnippet(report),
    reportType: report.reportType,
    powerSetup: report.reportType === 'power-availability'
      ? report.typedPreferences?.['power-availability']
      : undefined,
  });
  return fallback ? cleanupOverviewProse(fallback, report.executiveSummary, report.projectIntentAtGen, report.summary) || null : null;
}

function executiveSummarySummaryTrim(text: string): string | null {
  const first = text.trim().split(/\n\n+/)[0]?.trim();
  if (!first || first.length < 40) return null;
  return first;
}
